/**
 * useSupportedLanguages — Derives the list of supported languages from the
 * static locale JSON files shipped with the app.
 *
 * No backend API call needed — languages are determined at build time by
 * which locale files exist in src/locales/.
 *
 * English is always at the top of the list.
 */

import { useMemo } from 'react';
import { getAvailableLocales } from '../locales';
import { LANGUAGE_DISPLAY_MAP } from '../constants';
import type { LanguageOption } from '../types';

interface UseSupportedLanguagesResult {
    languages: LanguageOption[];
    loading: boolean;
    error: string | null;
}

const ENGLISH: LanguageOption = { code: 'en', name: 'English', flag: '🇺🇸' };

/** Reverse lookup: language code → display name + flag from LANGUAGE_DISPLAY_MAP */
function codeToOption(code: string): LanguageOption | null {
    for (const [displayName, entry] of Object.entries(LANGUAGE_DISPLAY_MAP)) {
        if (entry.code === code) {
            return { code: entry.code, name: displayName, flag: entry.flag };
        }
    }
    return null;
}

export function useSupportedLanguages(): UseSupportedLanguagesResult {
    const languages = useMemo(() => {
        const codes = getAvailableLocales();
        const result: LanguageOption[] = [ENGLISH];
        const seen = new Set<string>(['en']);

        for (const code of codes) {
            if (seen.has(code)) continue;
            const option = codeToOption(code);
            if (option) {
                result.push(option);
                seen.add(code);
            }
        }

        return result;
    }, []);

    return { languages, loading: false, error: null };
}
